import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faClock, faPen, faRotateRight, faTrash, faUtensils, faWandMagicSparkles, faXmark} from '@fortawesome/free-solid-svg-icons';
import {MealPlanEntry} from '../../../services/meal-plan/meal-plan.models';

/**
 * One card for every entry kind — recipe, free-text placeholder, and leftover
 * chip. The title is always resolved server-side, so this component never
 * branches to find a name; it only varies styling and which affordances show.
 */
@Component({
  selector: 'app-meal-entry-card',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: './entry-card.component.html',
  // Block host so vertical margins (e.g. the `space-y-*` gap between stacked
  // cards in a slot) actually apply — an inline host swallows them.
  host: {class: 'block'},
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntryCardComponent {
  @Input({required: true}) entry!: MealPlanEntry;

  /** Corner rounding. The pool uses a tighter radius than the grid chips. */
  @Input() rounded: 'md' | 'lg' = 'lg';

  /** Shows the "Rezept erstellen" affordance for free-text entries. Off by
   *  default — only the pool (where generated ideas live) opts in; a placed
   *  grid chip has no use for it. */
  @Input() showCreateRecipe = false;

  /** Recipe entries navigate to recipe detail; free-text / leftover do not. */
  @Output() open = new EventEmitter<MealPlanEntry>();
  @Output() edit = new EventEmitter<MealPlanEntry>();
  @Output() remove = new EventEmitter<MealPlanEntry>();
  @Output() retry = new EventEmitter<MealPlanEntry>();
  /** "Rezept erstellen" — author a full recipe from a free-text idea and
   *  attach it back to this card. Only ever emitted for `isFreeText` entries;
   *  callers decide whether to show/bind the affordance at all. */
  @Output() createRecipe = new EventEmitter<MealPlanEntry>();

  protected readonly faClock = faClock;
  protected readonly faPen = faPen;
  protected readonly faTrash = faTrash;
  protected readonly faUtensils = faUtensils;
  protected readonly faRotateRight = faRotateRight;
  protected readonly faXmark = faXmark;
  protected readonly faWandMagicSparkles = faWandMagicSparkles;

  get isLeftover(): boolean {
    return this.entry.portionOfEntryId != null;
  }

  get isRecipe(): boolean {
    return this.entry.recipe != null && !this.isLeftover;
  }

  get isFreeText(): boolean {
    return this.entry.recipe == null && !this.isLeftover;
  }

  get isNavigable(): boolean {
    return this.entry.recipe != null && !this.entry.pending && !this.entry.failed;
  }

  /** Provenance badge label, or null for the default `manual` source (no badge). */
  get sourceLabel(): string | null {
    switch (this.entry.source) {
      case 'assistant':
        return 'KI';
      case 'system':
        return 'Vorschlag';
      default:
        return null;
    }
  }

  /** e.g. 2 when a 2-serving recipe is planned for 4. */
  get servingsMultiplier(): number | null {
    const r = this.entry.recipe;
    if (!r || this.entry.servings == null || !r.servings) return null;
    const factor = this.entry.servings / r.servings;
    return factor > 1 && Number.isFinite(factor) ? factor : null;
  }

  get totalMinutes(): number | null {
    const r = this.entry.recipe;
    if (!r) return null;
    const total = r.totalTimeMinutes ?? 0;
    return total > 0 ? total : null;
  }

  onBodyClick(): void {
    if (this.isNavigable) this.open.emit(this.entry);
  }
}
